import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Modal, ModalBody, ModalHeader } from 'reactstrap'
import { motion } from 'framer-motion'
import CustomButton from 'components/Buttons/CustomButton'
import PMBSelect from 'components/PMBSelect'
import { setIsModalOpenAddAddressBook } from '../address-book/addressBookSlice'
import {
  useShareProjectMutation,
  useRevokeAccessToProjectMutation,
} from './projectsMenuListApiSlice'
import ShareContactPicker, {
  buildParticipantFromContact as buildContactParticipant,
  getContactUserId,
} from './ShareContactPicker'
import ModalAddContact from '../address-book/ModalAddContact'
import { enqueueSnackbar } from 'notistack'

const roleOptions = [
  { value: 'EDITOR', label: 'Editor' },
  { value: 'VIEWER', label: 'Lihat Saja' },
]

const isMultipleShareEnabled = true

const ModalShareProject = ({
  isOpen,
  toggle,
  project,
  existingParticipants,
  refetch,
}) => {
  const dispatch = useDispatch()
  const addressBookRef = useRef(null)
  const { isModalOpenAddAddressBook } = useSelector((e) => e.addressBook)
  const [shareProjectApi, { isLoading }] = useShareProjectMutation()
  const [revokeAccess, { isLoading: isRevoking }] =
    useRevokeAccessToProjectMutation()

  const [participants, setParticipants] = useState([])

  useEffect(() => {
    if (isOpen) {
      const list = Array.isArray(existingParticipants)
        ? existingParticipants
        : []

      const filtered = list.filter((p) => p.role !== 'OWNER')

      const mappedParticipants = filtered.map((p, index) => ({
        key: p.userId ? `user-${p.userId}` : `existing-${index}`,
        userId: p.userId,
        name: p.name,
        email: p.email,
        role: p.role || 'VIEWER',
        profileImg: p.profileImg,
      }))

      setParticipants(
        isMultipleShareEnabled
          ? mappedParticipants
          : mappedParticipants.slice(0, 1),
      )
    } else {
      setParticipants([])
    }
  }, [isOpen, existingParticipants])

  const buildParticipantFromContact = (contact) => ({
    ...buildContactParticipant(contact),
    userId: getContactUserId(contact),
    role: 'VIEWER',
  })

  const handleSelectMultipleContact = (contact) => {
    const userId = getContactUserId(contact)
    if (!userId) {
      enqueueSnackbar('Kontak ini belum memiliki akun terdaftar.', {
        variant: 'warning',
      })
      return
    }

    const newParticipant = buildParticipantFromContact(contact)
    setParticipants((prev) => {
      const exists = prev.find(
        (p) =>
          (userId && p.userId === userId) ||
          (newParticipant.key && p.key === newParticipant.key),
      )
      if (exists) return prev

      return [...prev, newParticipant]
    })
  }

  const handleSelectSingleContact = (contact) => {
    const userId = getContactUserId(contact)
    if (!userId) {
      enqueueSnackbar('Kontak ini belum memiliki akun terdaftar.', {
        variant: 'warning',
      })
      return
    }
    setParticipants([buildParticipantFromContact(contact)])
  }

  const handleSelectContact = (contact) => {
    const userId = getContactUserId(contact)
    if (!userId) {
      enqueueSnackbar('Kontak ini belum memiliki akun terdaftar.', {
        variant: 'warning',
      })
      return
    }

    // Future use: multiple flow tetap disiapkan, tapi sekarang dimatikan dulu
    if (isMultipleShareEnabled) {
      handleSelectMultipleContact(contact)
      return
    }

    handleSelectSingleContact(contact)
  }

  const handleRoleChange = (userIdOrKey, role) => {
    setParticipants((prev) =>
      prev.map((p) =>
        p.userId === userIdOrKey || p.key === userIdOrKey
          ? { ...p, role }
          : p,
      ),
    )
  }

  const handleRemoveParticipant = async (userIdOrKey) => {
    const participant = participants.find(
      (p) => p.userId === userIdOrKey || p.key === userIdOrKey,
    )
    const userId =
      participant?.userId ||
      (typeof userIdOrKey === 'number' ? userIdOrKey : null)

    // If participant already exists on server (from existingParticipants), revoke access
    const isExisting = existingParticipants?.some(
      (p) => userId && String(p.userId) === String(userId),
    )
    if (isExisting && project?.id && userId) {
      try {
        await revokeAccess({
          resourceType: 'PROJECT',
          resourceId: project?.id,
          userIds: [userId],
        }).unwrap()
        enqueueSnackbar('Akses berhasil dicabut.', { variant: 'success' })
        if (refetch) {
          try {
            await refetch()
          } catch (e) {
            console.error('Failed to refetch after revoking access', e)
          }
        }
      } catch (error) {
        enqueueSnackbar(
          error?.data?.meta?.message ||
          error?.data?.message ||
          'Gagal mencabut akses',
          { variant: 'error' },
        )
        return
      }
    }
    setParticipants((prev) =>
      prev.filter((p) => p.userId !== userIdOrKey && p.key !== userIdOrKey),
    )
  }

  const handleSubmit = async () => {
    try {
      const selectedParticipants = isMultipleShareEnabled
        ? participants
        : participants.slice(0, 1)

      const roleGroups = {}
      for (const p of selectedParticipants) {
        if (!p.userId) continue
        if (!roleGroups[p.role]) {
          roleGroups[p.role] = []
        }
        if (!roleGroups[p.role].includes(p.userId)) {
          roleGroups[p.role].push(p.userId)
        }
      }

      const totalUserIds = Object.values(roleGroups).flat()
      if (totalUserIds.length === 0) {
        enqueueSnackbar('Pilih setidaknya satu penerima yang memiliki akun.', {
          variant: 'warning',
        })
        return
      }

      for (const [role, userIds] of Object.entries(roleGroups)) {
        await shareProjectApi({
          resourceType: 'PROJECT',
          resourceId: project?.id,
          userIds,
          role,
        }).unwrap()
      }

      enqueueSnackbar('Project berhasil dibagikan!', { variant: 'success' })
      if (refetch) {
        try {
          await refetch()
        } catch (e) {
          console.error('Failed to refetch after sharing project', e)
        }
      }
      toggle()
    } catch (error) {
      enqueueSnackbar(
        error?.data?.meta?.message ||
        error?.data?.message ||
        'Gagal membagikan project',
        { variant: 'error' },
      )
    }
  }

  const toggleAddContact = () => {
    dispatch(setIsModalOpenAddAddressBook(!isModalOpenAddAddressBook))
  }

  return (
    <>
      <ModalAddContact
        isOpen={isModalOpenAddAddressBook}
        toggle={toggleAddContact}
        isOutsideAddressBook
      />

      <Modal
        centered
        isOpen={isOpen}
        toggle={toggle}
        size="lg"
        data-testid="project-modal-share"
      >
        <ModalHeader toggle={toggle}>
          <span>Bagikan Project {project?.title}</span>
        </ModalHeader>

        <ModalBody>
          <motion.div
            layout="size"
            transition={{ layout: { duration: 0.22, ease: 'easeOut' } }}
          >
            <ShareContactPicker
              isOpen={isOpen}
              participants={participants}
              onSelectContact={handleSelectContact}
              onRemove={handleRemoveParticipant}
              inputRef={addressBookRef}
              renderActions={(p) => (
                <div style={{ minWidth: 140 }}>
                  <PMBSelect
                    value={roleOptions.find((r) => r.value === p.role)}
                    onChange={(opt) => handleRoleChange(p.key || p.userId, opt.value)}
                    options={roleOptions}
                    label=""
                  />
                </div>
              )}
            />

            <div className="d-flex gap-2 mt-4">
              <CustomButton
                block
                className="btn-cancel"
                onClick={toggle}
                testId="project-modal-share-cancel-button"
              >
                Batal
              </CustomButton>

              <CustomButton
                block
                color="primary"
                onClick={handleSubmit}
                disabled={participants.length === 0 || isLoading || isRevoking}
                testId="project-modal-share-submit-button"
              >
                {isLoading ? 'Menyimpan...' : 'Simpan'}
              </CustomButton>
            </div>
          </motion.div>
        </ModalBody>
      </Modal>
    </>
  )
}

export default ModalShareProject
